import { queryOptions } from '@tanstack/react-query';

import { queryClient } from '@/services/query-client';

import { fetchShipTypes } from '../data-provider';
import type { ShipType, ShipTypeFilters, ShipTypes } from '../models';
import { CACHE_KEYS } from '../types';
import { logger } from '../utils/logger';

/**
 * Internal ship type lookup cache used to resolve ship type references
 * without repeatedly requesting the same ship type data.
 *
 * Backed by React Query today, but can be migrated to another storage
 * implementation (e.g. IndexedDB) without changing consumers.
 */

export const shipTypesQueryOptions = (
  filters: Partial<ShipTypeFilters> = {},
  { enabled = true }: { enabled?: boolean } = {},
) =>
  queryOptions({
    enabled,
    queryFn: async ({ signal }) => {
      const shipTypes = await fetchShipTypes(filters, { signal });
      put(shipTypes.list);
      return shipTypes;
    },
    queryKey: CACHE_KEYS.shipTypes(filters),
    // biome-ignore lint/style/noMagicNumbers: 5 minutes
    staleTime: 1000 * 60 * 5,
  });

const INTERNAL_SHIP_TYPES_CACHE_KEY = btoa('internal-ship-types-cache-key');

const INTERNAL_QUERY_SHIP_TYPES_KEY = shipTypesQueryOptions({ codeDesc: INTERNAL_SHIP_TYPES_CACHE_KEY }).queryKey;

const getShipTypeFallback = (shipTypeId: string): ShipType => {
  logger.warn(`Ship type "${shipTypeId}" was not found. Verify the data model.`);
  return {
    codeDesc: shipTypeId,
    id: shipTypeId,
    label: shipTypeId,
    lookupCodeType: 'SHIPTYPE',
  };
};

export const put = async (shipTypes: ShipType[]) => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_SHIP_TYPES_KEY);

  const newShipTypes = shipTypes.reduce(
    (acc, shipType) => {
      acc[String(shipType.id)] = shipType;
      return acc;
    },
    { ...(data?.map ?? {}) },
  );

  queryClient.setQueryData(INTERNAL_QUERY_SHIP_TYPES_KEY, {
    list: Object.values(newShipTypes),
    map: newShipTypes,
  });
};

export const getAll = (): ShipTypes | undefined => {
  return queryClient.getQueryData(INTERNAL_QUERY_SHIP_TYPES_KEY);
};

export const find = async (shipTypeId: string): Promise<ShipType> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_SHIP_TYPES_KEY);

  const shipType = data?.map[shipTypeId];
  if (shipType) {
    return shipType;
  }

  try {
    const newData = await queryClient.query(shipTypesQueryOptions({ search: { id: shipTypeId } }));
    if (newData.map[shipTypeId]) {
      void put(newData.list);
      return newData.map[shipTypeId];
    }
  } catch {
    return getShipTypeFallback(shipTypeId);
  }

  return getShipTypeFallback(shipTypeId);
};

export const findAll = async (shipTypeIds: string[]): Promise<ShipType[]> => {
  const data = queryClient.getQueryData(INTERNAL_QUERY_SHIP_TYPES_KEY);

  const missingIds: string[] = [];
  const shipTypes: ShipType[] = [];

  for (const shipTypeId of shipTypeIds) {
    const shipType = data?.map[shipTypeId];
    if (shipType) {
      shipTypes.push(shipType);
    } else {
      missingIds.push(shipTypeId);
    }
  }

  if (!missingIds.length) {
    return shipTypes;
  }

  try {
    const newData = await queryClient.query(shipTypesQueryOptions({ search: { id: missingIds.join(',') } }));
    void put(newData.list);
    for (const missingId of missingIds) {
      const shipType = newData.map[missingId];
      if (shipType) {
        shipTypes.push(shipType);
      } else {
        shipTypes.push(getShipTypeFallback(missingId));
      }
    }
  } catch {
    for (const missingId of missingIds) {
      shipTypes.push(getShipTypeFallback(missingId));
    }
  }

  return shipTypes;
};
